import React, {useState, useContext, useEffect, useRef} from 'react';
import {FiPlus} from 'react-icons/fi';
import ContactModal from '../../components/modals/ContactModal';
import DeleteModal from '../../components/modals/DeleteModal';
import {ContactContext} from '../../context/ContactProvider';
import saveIcon from '../../assets/icons/save.svg';
import SuccessNotification from '../../components/toast/SuccessNotification';
import ErrorNotification from '../../components/toast/ErrorNotification';

export default function VendorContact({vendorId, vendorName}) {
  const {
    contacts,
    contactId,
    getContacts,
    getContact,
    deleteContact,
    createContact,
    updateContact,
    getContactTypes,
    setContacts,
    selectedContact,
    setSelectedContact,
    callContactForRefresh,
    setCallContactForRefresh,
    token,
    showContactModalRef,
    updatedContactId,
  } = useContext(ContactContext);
  const [addingContact, setAddingContact] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [localContacts, setLocalContacts] = useState([]);
  const [deleteContactId, setDeleteContactId] = useState(contactId);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSavedText, setShowSavedText] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [text, setText] = useState('');
  const [technical, setTechnical] = useState(false);
  const [billing, setBilling] = useState(false);
  const [contactTypes, setContactTypes] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);
  const [isMediumScreen, setIsMediumScreen] = useState(window.innerWidth > 1300);
  const [isLargeScreen, setIsLargeScreen] = useState(window.innerWidth > 1740);
  const jobTitleInputRef = useRef(null);
  const dropdownContainerRef = useRef(null);

  useEffect(() => {
    showContactModalRef.current = showContactModal;
  }, [showContactModal, showContactModalRef]);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1200);
      setIsMediumScreen(window.innerWidth > 1300);
      setIsLargeScreen(window.innerWidth > 1740);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isSubmitting, isSmallScreen, isMediumScreen, isLargeScreen]);

  const closeNotification = () => {
    setShowSuccess(false);
    setShowError(false);
  };

  const handleRefreshNo = () => {
    setCallContactForRefresh(false);
  };

  const handleRefreshYes = async () => {
    setCallContactForRefresh(false);

    const updatedContacts = await getContacts(vendorId);
    setContacts(updatedContacts);
    setLocalContacts(updatedContacts);

    if (showContactModal && selectedContact?.rowKey === updatedContactId) {
      const updatedContact = await getContact(updatedContactId);
      setSelectedContact(updatedContact);
      setTechnical(updatedContact.technical || false);
      setBilling(updatedContact.billing || false);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const contactData = {
        ...selectedContact,
        vendorId: vendorId,
        company: selectedContact.company,
        technical: technical,
        billing: billing,
      };
      if (selectedContact.rowKey) {
        await updateContact(contactData);
        setShowSuccess(true);
        setText('Contact updated successfully');
      } else {
        await createContact(contactData);
        setShowSuccess(true);
        setText('Contact created successfully');
      }
      const updatedContacts = await getContacts(vendorId);
      setContacts(updatedContacts);
      setLocalContacts(updatedContacts);
      setShowSavedText(true);
      setShowContactModal(false);
      setTimeout(() => setShowSavedText(false), 2000);
    } catch (error) {
      console.error('Error creating/updating contact:', error);
      setShowError(true);
      setText(`${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddContact = () => {
    setAddingContact(true);
    setShowContactModal(true);
    setTechnical(false);
    setBilling(false);
    setSelectedContact({
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      jobTitle: '',
      company: vendorName,
      id: vendorId,
      notes: '',
    });
  };

  const handleViewContact = async (contactId) => {
    const contact = await getContact(contactId);
    setTechnical(contact.technical || false);
    setBilling(contact.billing || false);
    setSelectedContact(contact);
    setShowContactModal(true);
  };

  const handleInputChange = (event) => {
    const {name, value, type, checked} = event.target;
    if (type === 'checkbox') {
      if (name === 'technical') {
        setTechnical(checked);
      } else if (name === 'billing') {
        setBilling(checked);
      }
    } else {
      setSelectedContact((prevContact) => ({
        ...prevContact, [name]: value,
      }));
      if (name === 'jobTitle') {
        if (value.trim() !== '') {
          setIsDropdownOpen(true);
        } else {
          setIsDropdownOpen(false);
        }
      }
    }
  };

  const handleSelectJobTitle = (title) => {
    setSelectedContact((prevContact) => ({
      ...prevContact, jobTitle: title,
    }));
    setIsDropdownOpen(false);
  };

  const handleDeleteContactModal = (contactId) => {
    if (contactId) {
      setShowDeleteModal(true);
      setDeleteContactId(contactId);
    } else {
      console.error('Invalid contact ID:', contactId);
    }
  };

  const filteredContactTypes = contactTypes.filter((type) => {
    const jobTitleLower = (selectedContact?.jobTitle || '').toLowerCase();
    return type.toLowerCase().includes(jobTitleLower) || !jobTitleLower.trim();
  });

  function formatPhoneNumberDisplay(phoneNumber) {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    const formattedPhoneNumber = digitsOnly.replace(/(\d{3})(\d{3})(\d{4})/, '($1)-$2-$3');
    return formattedPhoneNumber;
  }

  function handlePhoneNumberInput(event) {
    const input = event.target;
    let inputValue = input.value.replace(/\D/g, '');
    if (inputValue.length > 10) {
      inputValue = inputValue.substring(0, 10);
    }
    input.value = formatPhoneNumberDisplay(inputValue);
    setSelectedContact((prevContact) => ({
      ...prevContact, phoneNumber: inputValue,
    }));
  }

  useEffect(() => {
    const fetchData = async () => {
      const fetchedContacts = await getContacts(vendorId);
      setContacts(fetchedContacts);
      setLocalContacts(fetchedContacts);
      const fetchedContactTypes = await getContactTypes(token);
      setContactTypes(fetchedContactTypes);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, token, setContacts]);


  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (jobTitleInputRef.current && dropdownContainerRef.current && !jobTitleInputRef.current.contains(event.target) && !dropdownContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      } else if (jobTitleInputRef.current && jobTitleInputRef.current.contains(event.target)) {
        setIsDropdownOpen(true);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => {
      window.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (callContactForRefresh) {
      if (showContactModal && selectedContact?.rowKey === updatedContactId) {
        setCallContactForRefresh(true);
      } else {
        handleRefreshYes();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callContactForRefresh, showContactModal, selectedContact, updatedContactId,]);

  return (<div className="modal mb-12 mt-3 flex h-full flex-col">
    <div className="modal-content flex-grow p-4">
      {contacts.length === 0 ? (<div
        className="mb-6 mt-6 flex w-1/3 flex-col items-center justify-center space-y-4 rounded-md bg-gray-100 py-32 text-center text-gray-500 dark:bg-stone-800 dark:text-gray-200 sm:w-1/3 md:w-1/2 lg:mx-auto lg:w-3/4">
        <p>No Contacts Added.</p>
        <button onClick={handleAddContact} className="primary-large">
          <FiPlus className="mr-2"/>
          Add Contact
        </button>
      </div>) : (<>
        <div className="mb-4 flex">
          <button onClick={handleAddContact} className="primary-large">
            <FiPlus className="mr-2"/>
            Add Contact
          </button>
        </div>
        <div
          className="h-[650px] overflow-x-auto"
        >
          <table className="w-full text-gray-600 dark:text-gray-300">
            <thead className="vendor-table-header">
            <tr className="text-left">
              <th className="vendor-table-label px-2">Full Name</th>
              <th className="vendor-table-label px-2">Company</th>
              <th className="vendor-table-label px-2">Email</th>
              <th className="vendor-table-label px-2">Phone Number</th>
              <th className="vendor-table-label px-2">Job Title</th>
              <th className="vendor-table-label px-2">Technical</th>
              <th className="vendor-table-label px-2">Billing</th>
            </tr>
            </thead>
            <tbody>
            {contacts.map((contact, index) => (<tr
              key={index}
              onClick={() => handleViewContact(contact.rowKey)}
              className="vendor-table-row"
            >
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-1"
                  title={`${contact.firstName} ${contact.lastName}`}
                >
                  {`${contact.firstName} ${contact.lastName}`.length > 15 ? `${contact.firstName} ${contact.lastName}`.substring(0, 15) + '...' : `${contact.firstName} ${contact.lastName}`}
                </p>
              </td>
              {/* Include all table cells as in your original code */}
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-1"
                  title={contact.company}
                >
                  {contact.company ? contact.company.length > 15 ? contact.company.substring(0, 15) + '...' : contact.company : 'None'}
                </p>
              </td>
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-1"
                  title={contact.email ? contact.email : 'None'}
                >
                  {contact.email && contact.email.length > 12 ? contact.email.substring(0, 12) + '...' : contact.email || 'None'}
                </p>
              </td>
              <td className="w-full px-2 py-1 md:w-1/4">
                <p className="w-full rounded-md px-2 py-1 md:px-1 md:py-1">
                  {contact.phoneNumber ? contact.phoneNumber : 'None'}
                </p>
              </td>
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-2"
                  title={contact.jobTitle}
                >
                  {contact.jobTitle && contact.jobTitle.length > 20 ? contact.jobTitle.substring(0, 20) + '...' : contact.jobTitle || 'None'}
                </p>
              </td>
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-1"
                  title={contact.technical}
                >
                  {contact.technical ? 'Yes' : 'No'}
                </p>
              </td>
              <td className="w-full px-2 py-1 md:w-1/4">
                <p
                  className="w-full truncate rounded-md px-2 py-1 hover:overflow-visible md:px-1 md:py-1"
                  title={contact.billing}
                >
                  {contact.billing ? 'Yes' : 'No'}
                </p>
              </td>
            </tr>))}
            </tbody>
          </table>
        </div>
      </>)}
    </div>
    {showContactModal && (<ContactModal
      isOpen={showContactModal}
      onClose={() => {
        setShowContactModal(false);
        setSelectedContact(null);
        setAddingContact(false);
        setShowSavedText(false);
      }}
      closeOnOutsideClick={!callContactForRefresh}
      submit={handleFormSubmit}
      vendor={vendorId}
      contact={selectedContact}
      title={addingContact ? 'Add New Contact' : 'Contact Details'}
    >
      <div className="max-h-600px max-w-full overflow-auto rounded-md bg-white px-2 py-4 dark:bg-stone-900 sm:py-0">
        <form className="space-y-4" onSubmit={handleFormSubmit}>
          {/* First Name and Last Name */}
          <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2">
            <div>
              <label
                htmlFor="firstName"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={selectedContact.firstName ? selectedContact.firstName : ''}
                onChange={handleInputChange}
                required
                maxLength="32"
                className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
              />
            </div>
            <div>
              <label
                htmlFor="lastName"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={selectedContact.lastName ? selectedContact.lastName : ''}
                onChange={handleInputChange}
                required
                maxLength="32"
                className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
              />
            </div>
          </div>
          {/* Email Address */}
          <div>
            <label
              htmlFor="email"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={selectedContact.email ? selectedContact.email : ''}
              placeholder="Email Address"
              onChange={handleInputChange}
              required
              maxLength="100"
              className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
            />
          </div>
          {/* Phone Number and Company */}
          <div className="grid grid-cols-2 gap-4">
            <div className="inline-block">
              <label
                htmlFor="phoneNumber"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Phone Number
              </label>
              <input
                type="tel"
                maxLength="16"
                name="phoneNumber"
                placeholder={'Phone Number'}
                value={formatPhoneNumberDisplay(selectedContact.phoneNumber || '')}
                onInput={handlePhoneNumberInput}
                className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
              />
            </div>
            <div className="inline-block">
              <label
                htmlFor="company"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Company
              </label>
              <input
                type="text"
                maxLength="1024"
                name="company"
                placeholder="Company"
                value={selectedContact.company || ''}
                onChange={handleInputChange}
                className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
              />
            </div>
          </div>
          {/* Job Title and Switches */}
          <div className="flex space-x-4">
            <div className="flex flex-col" style={{flex: 2}}>
              <label
                htmlFor="jobTitle"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Job Title
              </label>
              <input
                type="text"
                maxLength="100"
                name="jobTitle"
                value={selectedContact.jobTitle || ''}
                onChange={handleInputChange}
                ref={jobTitleInputRef}
                className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
                placeholder="Job Title"
                autoComplete="off"
              />
              {isDropdownOpen && (<div
                className="absolute z-50 mt-16 overflow-y-auto rounded-md border border-gray-300 bg-white shadow-md dark:border-gray-700 dark:bg-stone-900"
                style={{maxHeight: '200px'}}
                ref={dropdownContainerRef}
              >
                {filteredContactTypes.map((type, index) => (<div
                  key={index}
                  onClick={() => handleSelectJobTitle(type)}
                  className="cursor-pointer px-2 py-1 hover:bg-blue-100 dark:hover:bg-stone-700"
                >
                  {type}
                </div>))}
              </div>)}
            </div>
            <div className="relative flex flex-col">
              <label className="mb-3 block w-16 text-sm font-medium text-gray-700 dark:text-gray-300">
                Technical?
              </label>
              <label
                className={`switch inline-flex w-14 cursor-pointer items-center rounded-md px-1 ${technical ? 'bg-blue-400 dark:bg-indigo-500' : 'bg-gray-200'}`}
              >
                <input
                  className="hidden"
                  type="checkbox"
                  name="technical"
                  checked={technical}
                  onChange={handleInputChange}
                />
                <span
                  className={`block h-4 w-4 rounded-full bg-white shadow-inner transition-transform duration-200 ease-in-out ${technical ? 'translate-x-9 transform' : ''}`}
                ></span>
                <span
                  className={`text-xs ${technical ? 'text-white' : 'text-gray-400 dark:text-gray-600'}`}
                  style={{userSelect: 'none'}}
                >
                      {technical ? ' Yes' : ' No'}
                    </span>
              </label>
            </div>
            <div className="relative flex flex-col">
              <label className="mb-3 block w-20 text-sm font-medium text-gray-700 dark:text-gray-300">
                Billing?
              </label>
              <label
                className={`switch inline-flex w-14 cursor-pointer items-center rounded-md px-1 ${billing ? 'bg-blue-400 dark:bg-indigo-500' : 'bg-gray-200'}`}
              >
                <input
                  className="hidden"
                  type="checkbox"
                  name="billing"
                  checked={billing}
                  onChange={handleInputChange}
                />
                <span
                  className={`block h-4 w-4 rounded-full bg-white shadow-inner transition-transform duration-200 ease-in-out ${billing ? 'translate-x-9 transform' : ''}`}
                ></span>
                <span
                  className={`text-center text-xs ${billing ? 'text-white' : 'text-gray-400 dark:text-gray-600'}`}
                  style={{userSelect: 'none'}}
                >
                      {billing ? 'Yes' : 'No'}
                    </span>
              </label>
            </div>
          </div>
          {/* Notes */}
          <div>
            <label
              htmlFor="notes"
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Notes
            </label>
            <textarea
              name="notes"
              maxLength="1024"
              value={selectedContact.notes ? selectedContact.notes : ''}
              onChange={handleInputChange}
              className="w-full rounded-md border px-3 py-2 dark:border-gray-700 dark:bg-stone-700"
              rows="2"
            ></textarea>
          </div>
          {/* Buttons */}
          <div className="justify-end bg-white pb-4 pt-2 dark:bg-stone-900 sm:flex sm:flex-row-reverse">
            {selectedContact.rowKey && (<button
              onClick={(e) => {
                e.preventDefault();
                handleDeleteContactModal(selectedContact.rowKey);
              }}
              type="button"
              className="mt-1 inline-flex w-36 justify-center rounded-md border border-transparent bg-red-500 py-2.5 text-base font-medium text-white shadow-sm hover:bg-red-700 sm:mx-1 sm:mt-0 sm:text-sm"
            >
              {/* Delete Icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2.5}
                stroke="currentColor"
                className="mr-1 inline-block h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              Delete
            </button>)}
            <button
              type="submit"
              className="mt-1 inline-flex w-36 justify-center rounded-md bg-blue-600 py-2.5 text-base font-medium text-white shadow-sm hover:bg-blue-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:mx-1 sm:mt-0 sm:text-sm"
            >
              {showSavedText ? (<div
                className="mr-2.5 inline-block h-3 w-3 animate-spin rounded-full border-b-2 border-gray-300"/>) : (<img
                src={saveIcon}
                alt="Export"
                className="mr-1.5 inline-block h-4 w-4"
              />)}
              {showSavedText ? 'Saving' : selectedContact.rowKey ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </ContactModal>)}
    {showDeleteModal && (<DeleteModal
      isOpen={showDeleteModal}
      onClose={() => {
        setShowDeleteModal(false);
        setSelectedContact(null);
      }}
      onDelete={() => {
        deleteContact(deleteContactId, vendorId);
        setShowDeleteModal(false);
      }}
      title="Delete Contact"
      description="Are you sure you want to delete this contact?"
    />)}
    {showSuccess && (<SuccessNotification
      show={showSuccess}
      onClose={closeNotification}
      text={text}
    />)}
    {showError && (<ErrorNotification
      show={showError}
      onClose={closeNotification}
      text={text}
    />)}
    {callContactForRefresh && (<div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center pt-8 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity"
          aria-hidden="true"
        >
          <div className="absolute inset-0 bg-stone-950 bg-opacity-75"></div>
        </div>
        <span
          className="hidden sm:inline-block sm:h-screen sm:align-middle"
          aria-hidden="true"
        >
              &#8203;
            </span>
        <div
          className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all dark:bg-stone-800 sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <div className="flex h-full flex-col">
            <div className="flex flex-grow items-center justify-center bg-white px-4 py-6 dark:bg-stone-800 sm:p-6">
              <div className="sm:flex sm:items-center">
                <div className="text-center sm:text-left">
                  <h3
                    className="pt-6 text-lg font-semibold leading-6 text-gray-900 dark:text-gray-200"
                    id="modal-headline"
                  >
                    Changes have been made, please refresh.
                  </h3>
                </div>
              </div>
            </div>
            <div
              className="justify-center bg-white px-4 py-3 dark:bg-stone-800 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                onClick={handleRefreshYes}
                type="button"
                className="mt-3 inline-flex w-28 justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none dark:bg-indigo-500 dark:hover:bg-indigo-600 sm:ml-2 sm:mt-0 sm:text-sm"
              >
                Refresh
              </button>
              <button
                onClick={handleRefreshNo}
                type="button"
                className="mt-3 inline-flex w-28 justify-center rounded-md border border-transparent bg-red-500 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none sm:mx-1 sm:mt-0 sm:text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>)}
  </div>);
}
